import React from "react";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import Seo from "@/components/Seo";

function LegalShell({ title, updated, children, testid }) {
  return (
    <MarketingLayout>
      <Seo title={`${title} · Yosi Media Studio`} description={`${title} for Yosi Media Studio — Huntsville, Alabama.`} />
      <section className="max-w-3xl mx-auto px-6 sm:px-8 py-20 text-slate-300 leading-relaxed" data-testid={testid}>
        <div className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: "#2B87F5" }}>Legal</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl text-white mb-3">{title}</h1>
        <p className="text-xs text-slate-500 mb-10">Last updated: {updated}</p>
        <div className="space-y-6 text-sm">{children}</div>
      </section>
    </MarketingLayout>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalShell title="Privacy Policy" updated="February 2026" testid="privacy-page">
      <p>Yosi Media Studio ("we", "us") respects your privacy. This policy explains what personal information we collect through our website and services, how we use it, and the choices you have.</p>
      <h2 className="font-semibold text-slate-100 text-base">Information we collect</h2>
      <p>When you contact us or request a free growth audit, we collect your name, business name, email, phone, and any information you provide about your business goals. We also collect anonymous analytics (page views, referrer, device) to improve the site.</p>
      <h2 className="font-semibold text-slate-100 text-base">How we use it</h2>
      <p>Contact information is used only to respond to your inquiry and follow up about your project. We do not sell your data to third parties. If we hire third-party processors (email delivery, analytics, payment processing), they are bound by their own privacy commitments.</p>
      <h2 className="font-semibold text-slate-100 text-base">Your choices</h2>
      <p>You can request a copy of your data or ask us to delete it at any time by emailing <a className="text-blue-400" href="mailto:info@yosimediastudio.com">info@yosimediastudio.com</a>.</p>
      <h2 className="font-semibold text-slate-100 text-base">Contact</h2>
      <p>Yosi Media Studio · Huntsville, Alabama · <a className="text-blue-400" href="mailto:info@yosimediastudio.com">info@yosimediastudio.com</a> · 256-288-8270</p>
    </LegalShell>
  );
}

export function TermsOfService() {
  return (
    <LegalShell title="Terms of Service" updated="February 2026" testid="terms-page">
      <p>By using yosimediastudio.com or engaging Yosi Media Studio for services, you agree to these terms.</p>
      <h2 className="font-semibold text-slate-100 text-base">Services</h2>
      <p>Deliverables, timelines, and pricing are defined in the signed proposal or written scope specific to each engagement. This website's descriptions are informational; the signed proposal governs the working relationship.</p>
      <h2 className="font-semibold text-slate-100 text-base">Intellectual property</h2>
      <p>Unless otherwise stated in a signed proposal, on final payment you own the deliverables produced for you. Third-party assets (fonts, stock imagery, plugins) remain licensed per their vendor's terms.</p>
      <h2 className="font-semibold text-slate-100 text-base">Limitation of liability</h2>
      <p>Our aggregate liability for any claim is limited to the fees paid for the specific service in the 12 months preceding the claim.</p>
      <h2 className="font-semibold text-slate-100 text-base">Contact</h2>
      <p><a className="text-blue-400" href="mailto:info@yosimediastudio.com">info@yosimediastudio.com</a> · 256-288-8270</p>
    </LegalShell>
  );
}

export function CookiePolicy() {
  return (
    <LegalShell title="Cookie Policy" updated="February 2026" testid="cookies-page">
      <p>This website uses a small number of cookies to keep the experience working and to measure traffic anonymously.</p>
      <h2 className="font-semibold text-slate-100 text-base">Essential cookies</h2>
      <p>These are required for basic site functionality (session, language preference, form submission). They cannot be disabled without breaking the site.</p>
      <h2 className="font-semibold text-slate-100 text-base">Analytics cookies</h2>
      <p>We use anonymized analytics to understand which pages are useful. No personally identifying information is collected. You may block these in your browser without impacting the site.</p>
      <h2 className="font-semibold text-slate-100 text-base">Contact</h2>
      <p><a className="text-blue-400" href="mailto:info@yosimediastudio.com">info@yosimediastudio.com</a></p>
    </LegalShell>
  );
}

export function PaymentPolicy() {
  return (
    <LegalShell title="Payment & Refund Policy" updated="February 2026" testid="payment-policy-page">
      <p>All payments for Yosi Media Studio services are processed securely through Stripe. Invoicing terms are defined in each signed proposal.</p>
      <h2 className="font-semibold text-slate-100 text-base">One-time projects</h2>
      <p>Custom website and branding engagements typically require a deposit at signing, with the balance billed at defined milestones. Deposits are non-refundable once discovery/design work has begun.</p>
      <h2 className="font-semibold text-slate-100 text-base">Recurring services</h2>
      <p>Monthly services (hosting, maintenance, SEO retainers) are billed on the invoice cycle described in the signed proposal. Cancel any time — you'll retain access until the end of the current paid period.</p>
      <h2 className="font-semibold text-slate-100 text-base">Refunds</h2>
      <p>If you believe a charge was made in error, email us within 30 days of the charge at <a className="text-blue-400" href="mailto:info@yosimediastudio.com">info@yosimediastudio.com</a> and we'll investigate and refund if warranted.</p>
      <h2 className="font-semibold text-slate-100 text-base">Contact</h2>
      <p>Yosi Media Studio · Huntsville, Alabama · 256-288-8270</p>
    </LegalShell>
  );
}
